// What the admin screens (Overview, People, Councils, Trades, Jobs, Email campaigns) ask the database, and the words and
// number formats they show. The database functions refuse anyone who isn't an admin, so none of this is a gate.

import { cloudSetupMessage } from '../cloudMessage.js';

async function call(client, name, args) {
  if (!client) throw new Error(cloudSetupMessage());
  const { data, error } = await client.rpc(name, args);
  if (error) {
    const message = String(error.message || '');
    if (message.includes('ADMIN_ONLY')) throw new Error('Only admins can see this.');
    if (message.includes('NOT_FOUND')) throw new Error('That record no longer exists.');
    if (message.includes('ALREADY_SCHEDULED')) throw new Error("That provider's email is already booked in Resend — cancel it there first.");
    if (message.includes('NOT_PLANNED')) throw new Error('That provider is no longer in the plan.');
    if (message.includes('NOT_FLAGGED')) throw new Error("That provider isn't flagged any more.");
    if (message.includes('NOT_LISTABLE')) throw new Error("That can't go on BedMaps. Refresh the page and check their status.");
    if (message.includes('provider_partners_referral_email_check') || message.includes('provider_partners_contact_email_check')) throw new Error('Check the email addresses: one of them is not an email address.');
    throw new Error(message || 'Could not load. Please try again.');
  }
  return data;
}

/**
 * Whether the dashboard shows trades: the Trades tab, the Trades figures on the overview and the links that open a trade company.
 * Off for now (nothing about trades is tracked yet). Set it to true to bring all of it back; the code behind it is untouched.
 */
export const showTrades = false;

/**
 * Whether the outreach screens show follow-up emails: the "Follow-ups due" and "Finished" figures, the Step and Next follow-up
 * columns, and steps 2 and 3 of the playbook. Off for now (only the first email is sent). Set it to true to bring them back;
 * the code behind it, and the follow-up tracking in outreachTracking.js, are untouched.
 */
export const followUpsOn = false;

export const loadOverview = (client) => call(client, 'staff_admin_overview');

export const listPeople = (client, { type = 'all', query = '', offset = 0, limit = 50 } = {}) => call(client, 'staff_list_people', {
  p_type: type, p_query: query, p_limit: limit, p_offset: offset,
});

/** Opening a record is logged by the database; `sensitive` also asks for the private details (logged as such). */
export const loadPerson = (client, id, { sensitive = false } = {}) => call(client, 'staff_person_record', { p_user: id, p_sensitive: sensitive });

export const listTrades = (client, { query = '', offset = 0, limit = 50 } = {}) => call(client, 'staff_list_trades', {
  p_query: query, p_limit: limit, p_offset: offset,
});

export const loadTrade = (client, id) => call(client, 'staff_trade_record', { p_org: id });

/**
 * Housing providers we are reaching out to (not accounts on Resident Service 360). `council` is an ONS code; `selfReferral: true`
 * keeps only those accepting self-referrals; `outreach` is 'all', 'flagged' (dropped from outreach) or 'not_flagged'.
 * `support` is one of `supportFilters` (a group, 'women_only', 'men_only' or 'not_tagged') and `accommodation` one of
 * `accommodationFilters`; '' (the default) is everyone.
 */
export const listProviderProspects = (client, {
  council = null, query = '', selfReferral = null, outreach = 'all', support = '', accommodation = '', offset = 0, limit = 50,
} = {}) => call(client, 'staff_list_provider_prospects', {
  p_council: council, p_query: query, p_self_referral: selfReferral, p_limit: limit, p_offset: offset, p_outreach: outreach,
  p_support: support || null, p_accommodation: accommodation || null,
});

/** Every council with housing providers or a housing team contact, and the broad areas that name no single council. */
export const listCouncils = (client, { query = '' } = {}) => call(client, 'staff_list_councils', { p_query: query });

export const listJobs = (client, { kind = 'all', status = 'all', query = '', offset = 0, limit = 50 } = {}) => call(client, 'staff_list_jobs', {
  p_kind: kind, p_status: status, p_query: query, p_limit: limit, p_offset: offset,
});

export const loadEmailCampaigns = (client) => call(client, 'staff_email_campaigns');

/** Naima's planned emails to housing providers, and the reserves that replace a provider who is dropped: `{ rows, reserves }`. */
export const loadOutreachPlan = (client) => call(client, 'staff_list_outreach_plan');

/**
 * Takes a provider out of the plan before anything is booked, and flags it (with the reason and an optional note) so it is left out of
 * future schedules. Answers `{ dropped, reason, replacement: { provider_id, organisation } | null }`.
 */
export const dropOutreachProvider = (client, providerId, { reason = 'other', note = '' } = {}) => call(client, 'staff_drop_outreach_provider', {
  p_provider_id: providerId, p_reason: reason, p_note: String(note ?? '').trim() || null,
});

/** Takes the flag off a provider, so it can be chosen for a future schedule again. Answers `{ provider_id, flagged: false }`. */
export const unflagOutreachProvider = (client, providerId) => call(client, 'staff_unflag_outreach_provider', { p_provider_id: providerId });

/**
 * The Roadmap tab's table, changed in the database rather than on the dashboard: `{ columns: [name], items: [{ [name]: text }],
 * updated_at }`, both lists in order.
 */
export const loadRoadmap = (client) => call(client, 'staff_load_roadmap');

/**
 * The Expenses tab's rows, changed in the database rather than on the dashboard: `{ items: [{ what, type, status, column, cost, ... }],
 * updated_at }`.
 */
export const loadExpenses = (client) => call(client, 'staff_load_expenses');

/**
 * The sponsorship or council outreach emails (`campaign` 'sponsorship' or 'councils'), copied in from Gmail, oldest first: each
 * `{ id, organisation, direction: 'sent'|'received', address, sender_name, subject, body, automatic, sent_at, mailbox, thread_id }`.
 */
export const loadCampaignEmails = (client, campaign) => call(client, 'staff_list_campaign_emails', { p_campaign: campaign });

/** The Investors, Funding or Revenue research (`kind` 'investors', 'funding' or 'revenue'): the whole list, as gathered. */
export const loadResearch = (client, kind) => call(client, 'staff_list_research', { p_kind: kind });

/** The Network tab's own contacts of one `type` (community, public_services, third_party or sponsors), by name. */
export const listNetworkContacts = (client, type) => call(client, 'staff_list_network_contacts', { p_type: type });

/** Adds a Network contact (no `id`) or changes one; `details` holds name, kind, area, contact_name, email, phone, website, note, description, address and postcode. */
export const saveNetworkContact = (client, id, type, details) => call(client, 'staff_save_network_contact', { p_id: id || null, p_type: type, p_details: details });

export const deleteNetworkContact = (client, id) => call(client, 'staff_delete_network_contact', { p_id: id });

/** Who is ticked to show on BedMaps' public pages: `{ partners: [provider id], contacts: [contact id] }`. */
export const bedmapsListingIds = (client) => call(client, 'staff_bedmaps_listing_ids');

/** Ticks (`show` true) or unticks partners (`kind` 'partner') or Network contacts ('contact') for BedMaps' public pages. */
export const setBedmapsListings = (client, kind, ids, show) => call(client, 'staff_set_bedmaps_listings', { p_kind: kind, p_ids: ids, p_show: show });

/** Housing providers who answered Naima: `{ counts: { accepted, no_capacity, declined }, rows }`, accepting ones first. */
export const listPartners = (client) => call(client, 'staff_list_partners');

/**
 * Adds or changes a partner from their reply; `status` null takes them off the list. `details` holds referral_email, contact_name,
 * contact_role, contact_email, contact_phone, requirements, note, reply_email_id and replied_on (blank ones are stored as nothing).
 */
export const setPartner = (client, providerId, status, details = {}) => call(client, 'staff_set_partner', {
  p_provider_id: providerId, p_status: status, p_details: details,
});

// The paged lists (people, housing providers, trades, jobs) are read in full so that a column can be sorted across all of them.

/** Rows asked for at a time. The database gives at most 200 per call. */
export const listPageSize = 200;
/** The most rows a list shows. */
export const listRowLimit = 5000;

/**
 * Every row of a paged list. `fetchPage({ offset, limit })` is the list function for one page, answering `{ rows, total, ... }`.
 * Pages are asked for one after another (offset 0, 200, 400…) until there are `total` rows or a page adds nothing, and never
 * past `listRowLimit` rows. Resolves `{ rows, total, capped, first }`: `total` is what was found (the database's count when
 * `capped` is true, because there were more than could be shown), and `first` is the first page, for anything else it carries.
 * `isCurrent()` is asked after each page: once it says no, the rest are not fetched and the result is null.
 * Rows are told apart by `identify(row)` (their `id`), so a list that shifts between pages doesn't show a row twice.
 */
export async function loadAllRows(fetchPage, { isCurrent = () => true, identify = (row) => row.id } = {}) {
  const rows = [];
  const seen = new Set();
  let first = null;
  let total = null;
  for (let offset = 0; ;) {
    const page = await fetchPage({ offset, limit: listPageSize });
    if (!isCurrent()) return null;
    const batch = Array.isArray(page?.rows) ? page.rows : [];
    first ??= page || {};
    total = numeric(page?.total) ?? total;
    const before = rows.length;
    for (const row of batch) {
      const id = identify(row);
      if (!seen.has(id)) { seen.add(id); rows.push(row); }
    }
    offset += batch.length;
    const reached = rows.length >= listRowLimit;
    // A page that adds nothing means the list is over (or shrank while we read it).
    if (reached || rows.length === before || (total !== null && rows.length >= total)) {
      const capped = reached && (total === null || total > listRowLimit);
      return { rows: rows.slice(0, listRowLimit), total: capped && total !== null ? total : Math.min(rows.length, listRowLimit), capped, first };
    }
  }
}

// Words. Anything we don't know is shown as it came, and nothing at all is shown as a dash.

/** Looks a value up in `labels`; unknown values come back as they are and blanks as `blank`. */
function labeller(labels, blank = '–') {
  return (key) => {
    if (key === null || key === undefined || key === '') return blank;
    return Object.hasOwn(labels, key) ? labels[key] : String(key);
  };
}

export const personTypes = [
  ['resident', 'Residents'],
  ['business_owner', 'Business owners'],
  ['trade', 'Trades'],
  ['buyer', 'Commercial buyers'],
  ['housing_provider', 'Housing providers'],
  ['staff', 'Staff'],
];

export const personTypeLabel = labeller({
  resident: 'Resident', business_owner: 'Business owner', trade: 'Trade', buyer: 'Commercial buyer', housing_provider: 'Housing provider', staff: 'Staff',
});

export const signedUpAsLabel = labeller({ resident: 'Resident', supplier: 'Trade', buyer: 'Commercial buyer' }, 'Not given');

export const memberRoleLabel = labeller({ owner: 'Owner', admin: 'Admin', member: 'Member' });

export const companyKindLabel = labeller({ supplier: 'Trade', buyer: 'Commercial buyer', housing_provider: 'Housing provider' });

export const jobKinds = [['all', 'All jobs'], ['home', 'Home jobs'], ['commercial', 'Commercial jobs'], ['tender', 'Private tenders']];

export const jobKindLabel = labeller({ home: 'Home job', commercial: 'Commercial job', tender: 'Private tender' });

export const jobStatuses = [['draft', 'Draft'], ['published', 'Open'], ['paused', 'Paused'], ['awarded', 'Awarded'], ['closed', 'Closed'], ['removed', 'Removed']];

export const jobStatusLabel = labeller(Object.fromEntries(jobStatuses));

export const workStatusLabel = labeller({
  awarded: 'Awarded', in_progress: 'In progress', completion_submitted: 'Waiting for sign-off', completed: 'Completed', disputed: 'Disputed', cancelled: 'Cancelled',
});

export const bidStatusLabel = labeller({ submitted: 'Sent', shortlisted: 'Shortlisted', declined: 'Declined', selected: 'Won', withdrawn: 'Withdrawn' });

export const planLabel = labeller({ subscribed: 'Subscribed', lapsed: 'Lapsed', free: 'Free bids' });

const planTones = { subscribed: 'good', lapsed: 'warn', free: 'neutral' };
export const planTone = (plan) => (Object.hasOwn(planTones, plan) ? planTones[plan] : 'neutral');

export const repairStatusLabel = labeller({
  submitted: 'Reported', acknowledged: 'Acknowledged', scheduled: 'Booked', in_progress: 'In progress', resolved: 'Fixed', closed: 'Closed', cancelled: 'Cancelled',
});

/** How sure we are that a housing provider lets rooms in shared houses, from the outreach research. */
export const evidenceLabel = labeller({ explicit_hmo: 'HMO confirmed', shared_house: 'Supported shared house', awaiting: 'To check' });

// Who a housing provider supports and the kind of accommodation it offers, tagged from the outreach research notes. Each list is
// `[value, words]` in the order the filters offer them; the values are what the database keeps.

export const supportGroups = [
  ['mental_health', 'Mental health'],
  ['substance_recovery', 'Drug and alcohol recovery'],
  ['homelessness', 'Homelessness'],
  ['prison_leaver', 'Prison leavers and probation'],
  ['domestic_abuse', 'Domestic abuse'],
  ['young_people', 'Young people'],
  ['leaving_care', 'Care leavers'],
  ['learning_disability', 'Learning disabilities'],
  ['autism', 'Autism'],
  ['physical_disability', 'Physical disabilities'],
  ['older_people', 'Older people'],
  ['refugee', 'Refugees and asylum seekers'],
  ['veterans', 'Veterans'],
];

export const supportGroupLabel = labeller(Object.fromEntries(supportGroups));

/** Whether a provider takes only women, only men, or says it takes both. */
export const genderFocusLabel = labeller({ women_only: 'Women only', men_only: 'Men only', mixed: 'Men and women' });

/** `ageRangeLabel(16, 25)` is "Aged 16–25", `ageRangeLabel(18, null)` "Aged 18+", `ageRangeLabel(null, 25)` "Up to 25"; no ages is ''. */
export function ageRangeLabel(min, max) {
  const low = numeric(min);
  const high = numeric(max);
  if (low !== null && high !== null) return `Aged ${low}–${high}`;
  if (low !== null) return `Aged ${low}+`;
  if (high !== null) return `Up to ${high}`;
  return '';
}

export const accommodationTypes = [
  ['supported_exempt', 'Supported (exempt) accommodation'],
  ['supported_living', 'Supported living'],
  ['dss_private', 'DSS / Universal Credit lets'],
  ['temporary', 'Temporary or emergency'],
  ['hostel', 'Hostel or night shelter'],
  ['refuge', 'Refuge'],
  ['approved_premises', 'Approved premises or CAS3'],
  ['move_on', 'Move-on'],
  ['housing_first', 'Housing First'],
];

/** The kind of room: in a shared house, or a flat of their own. */
export const unitTypes = [['shared_rooms', 'Rooms in shared houses'], ['self_contained', 'Self-contained flats']];

export const accommodationLabel = labeller(Object.fromEntries([...accommodationTypes, ...unitTypes]));

/** The choices for the "Who they support" filter. '' is everyone; 'not_tagged' is the providers with no group or gender found. */
export const supportFilters = [['', 'All'], ...supportGroups, ['women_only', 'Women only'], ['men_only', 'Men only'], ['not_tagged', 'Nothing found']];

/** The choices for the Accommodation filter. '' is everyone; 'not_tagged' is the providers with no accommodation type found. */
export const accommodationFilters = [['', 'All'], ...accommodationTypes, ...unitTypes, ['not_tagged', 'Nothing found']];

// Whether a provider's website shows the people behind it (a team, leadership, board or about page that names them), as found by
// scripts/providers/find_team_pages.py: the words, their tone, and where the status sorts (most people first, then the rest).
const teamStatuses = {
  team: ['Shows the team', 'good', 0],
  one_person: ['1 person named', 'warn', 0],
  no_names: ['About page, no names', 'neutral', -1],
  not_found: ['No team page', 'neutral', -2],
  site_down: ['Site down', 'bad', -3],
  no_website: ['No website', 'neutral', -4],
};

/** `{ label, tone }` for a team status; a provider not checked yet is null. */
export function teamStatus(value) {
  if (!value) return null;
  const [label, tone] = Object.hasOwn(teamStatuses, value) ? teamStatuses[value] : [String(value), 'neutral'];
  return { label, tone };
}

/** What the Team page column sorts by: the number of people named, then pages naming nobody, no page, a site down, no website; unchecked last. */
export function teamSortValue(row) {
  if (!row?.team_status) return null;
  const [, , rank] = Object.hasOwn(teamStatuses, row.team_status) ? teamStatuses[row.team_status] : [null, null, -5];
  return row.team_status === 'team' || row.team_status === 'one_person' ? numeric(row.team_people) ?? 1 : rank;
}

// How someone is referred to a provider, from its website (scripts/providers/check_provider_sites.py): the kind of form, its tone,
// and where it sorts (an online form first).
const referralForms = {
  online_form: ['Online form', 'good', 3],
  download: ['Form to download', 'good', 2],
  page_only: ['Referral page, no form', 'warn', 1],
  none: ['None found', 'neutral', 0],
};

/** `{ label, tone }` for a referral form; a site not checked (or that couldn't be read) is null. */
export function referralForm(value) {
  if (!value) return null;
  const [label, tone] = Object.hasOwn(referralForms, value) ? referralForms[value] : [String(value), 'neutral'];
  return { label, tone };
}

/** Who a referral form is for. */
export const referralTypeLabel = labeller({ self: 'Self-referral', agency: 'Agency or professional', family: 'Family or friends' });

/** What the Referral form column sorts by: an online form, a form to download, a page with no form, none found; unchecked last. */
export function referralSortValue(row) {
  if (!row?.referral_form) return null;
  return Object.hasOwn(referralForms, row.referral_form) ? referralForms[row.referral_form][2] : -1;
}

/** What a provider said to Naima's offer, as `[value, words, tone]` in the order they are offered. The values are what the database accepts. */
export const partnerStatuses = [
  ['accepted', 'Accepting referrals', 'good'],
  ['no_capacity', 'No spaces right now', 'warn'],
  ['declined', 'Declined', 'bad'],
];

/** `{ label, tone }` for a partner's status. */
export function partnerStatus(value) {
  const found = partnerStatuses.find(([key]) => key === value);
  return found ? { label: found[1], tone: found[2] } : { label: String(value || 'Unknown'), tone: 'neutral' };
}

/** Why a provider can be dropped from outreach, as `[value, words]` in the order they are offered. The values are what the database accepts. */
export const outreachReasons = [
  ['not_supported_housing', 'Not supported housing'],
  ['website_down', 'Website down or out of date'],
  ['not_a_fit', 'Not a fit for referrals'],
  ['already_in_touch', 'Already in touch'],
  ['other', 'Other'],
];

export const outreachReasonLabel = labeller(Object.fromEntries(outreachReasons));

/** Which email of the three a plan row is. */
export const outreachStepLabel = labeller({ 1: 'First email', 2: 'Follow-up 1', 3: 'Follow-up 2' });

const outreachStatuses = {
  planned: ['Planned', 'neutral'],
  scheduled: ['Booked in Resend', 'warn'],
  sent: ['Sent', 'good'],
  dropped: ['Dropped', 'bad'],
  cancelled: ['Cancelled', 'bad'],
};

/**
 * Where a housing provider stands with us (`outreach_stage` from staff_list_provider_prospects), as `[value, words, tone]`, from
 * nobody having written to them to their reply. Dropped from outreach comes last.
 */
export const outreachStages = [
  ['not_contacted', 'Not contacted', 'neutral'],
  ['contacted_before', 'Contacted before this list', 'neutral'],
  ['reserve', 'Reserve for the plan', 'neutral'],
  ['planned', 'In the email plan', 'neutral'],
  ['booked', 'Email booked', 'warn'],
  ['emailed', 'Emailed', 'good'],
  ['replied', 'Replied', 'good'],
  ['dropped', 'Dropped from outreach', 'bad'],
];

/** What a plan row's status says, and its tone: planned (only a plan), booked in Resend, sent, dropped or cancelled. */
export function outreachStatus(value) {
  const [label, tone] = Object.hasOwn(outreachStatuses, value) ? outreachStatuses[value] : [String(value || 'Unknown'), 'neutral'];
  return { label, tone };
}

export const idCheckLabel = labeller({
  pending: 'Waiting', approved: 'Verified', rejected: 'Rejected', cancelled: 'Cancelled', uploading: 'Started',
}, 'Not started');

const idCheckTones = { approved: 'good', rejected: 'bad', pending: 'warn' };
export const idCheckTone = (status) => (Object.hasOwn(idCheckTones, status) ? idCheckTones[status] : 'neutral');

export const applicationStatusLabel = labeller({
  waiting_for_id: 'Waiting for ID check',
  checking_availability: 'Checking the room is free',
  sent: 'Sent',
  seen: 'Seen',
  accepted: 'Accepted',
  declined: 'Declined',
  room_let: 'Room let',
  no_reply: 'No reply',
  withdrawn: 'Withdrawn',
});

// Numbers

/** A finite number, or null for anything that isn't one (including null, undefined and ''). */
function numeric(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function money(pounds, currency) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(pounds);
}

/** `formatMoney(123450)` is "£1,234.50": the database keeps money in pence. */
export function formatMoney(minor, currency = 'GBP') {
  const number = numeric(minor);
  return number === null ? '–' : money(number / 100, currency || 'GBP');
}

/** Bid prices are kept in pounds and can arrive as text: `formatPounds('180.5')` is "£180.50". */
export function formatPounds(value) {
  const number = numeric(value);
  return number === null ? '–' : money(number, 'GBP');
}

export function percentOf(part, whole) {
  const top = numeric(part);
  const bottom = numeric(whole);
  if (top === null || !bottom) return '–';
  return `${Math.round((top / bottom) * 100)}%`;
}

export function count(value) {
  return new Intl.NumberFormat('en-GB').format(numeric(value) ?? 0);
}

/** What `count` shows, as a number (a missing figure is the 0 on screen), for sorting a column of counts. */
export function countValue(value) {
  return numeric(value) ?? 0;
}

/** The London date of a timestamp or a plain date: `formatDay('2026-10-19T23:30:00Z')` is "20 October 2026". */
export function formatDay(value) {
  if (!value) return '–';
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return '–';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London' });
}

// The London day and time of a planned email, in words: "Tue 20 Oct · 09:30". The clocks change on 25 October, so this is not a fixed offset.
const slotFormat = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/London' });

function slotParts(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Object.fromEntries(slotFormat.formatToParts(date).map(({ type, value: part }) => [type, part]));
}

/** `formatSlotDay('2026-10-20T08:30:00+00:00')` is "Tue 20 Oct". */
export function formatSlotDay(value) {
  const parts = slotParts(value);
  return parts ? `${parts.weekday} ${parts.day} ${parts.month}` : '–';
}

/** `formatSlot('2026-10-20T08:30:00+00:00')` is "Tue 20 Oct · 09:30" (London time). */
export function formatSlot(value) {
  const parts = slotParts(value);
  return parts ? `${parts.weekday} ${parts.day} ${parts.month} · ${parts.hour}:${parts.minute}` : '–';
}

export function orDash(value) {
  return value === null || value === undefined || value === '' ? '–' : value;
}

// Links. The outreach research is typed in by hand, so a link is only offered when it really is a web address.

/** `webLink('www.example.org/housing')` is `{ href: 'https://www.example.org/housing', domain: 'example.org' }`; anything that isn't an http(s) address is null. */
export function webLink(value) {
  const text = String(value ?? '').trim();
  if (!text) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return { href: url.href, domain: url.hostname.replace(/^www\./i, '') };
  } catch {
    return null;
  }
}

/** `phoneHref('0121 303 7410')` is "tel:01213037410". With several numbers in one box, the first one is dialled. */
export function phoneHref(value) {
  const first = String(value ?? '').split(/[;,/]|\bor\b/i)[0];
  const dialable = first.replace(/[^\d+]/g, '');
  return /\d/.test(dialable) ? `tel:${dialable}` : null;
}
